import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { useNoticeBannersStackActions } from '@garnish/overlays';

/**
 * Shows a permanent notice banner when the order is late.
 * Banner is dismissed automatically when the component unmounts or `isLate` becomes false.
 */
export const useLateOrderNoticeBanner = (props: Props) => {
  const { isLate = false } = props;

  const { formatMessage } = useIntl();
  const notice = formatMessage(messages.lateOrderNotice);

  const { push: addNoticeBanner, remove: removeNoticeBanner } = useNoticeBannersStackActions();

  useEffect(() => {
    if (!isLate) return;

    addNoticeBanner(
      {
        id: bannerId,
        text: notice,
        palette: 'success',
        autoHideTimeout: 'never',
        onClose: () => removeNoticeBanner(bannerId),
      },
      true,
    );

    return () => {
      removeNoticeBanner(bannerId);
    };
  }, [isLate, notice, addNoticeBanner, removeNoticeBanner]);
};

// ─── Constants ────────────────────────────────────────────────────────────────

const bannerId = 'late-notice';

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  lateOrderNotice: {
    defaultMessage: "Sorry we're running a little behind with your order",
    description: 'Order Status | Late Order | Notice',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  isLate: boolean;
};
